import { cn } from "@/lib/utils";
import Avatar from "@imminently/decisively-ui/Avatar";
import Chip, { type ChipProps } from "@imminently/decisively-ui/Chip";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import type { Job } from "@packages/commons";
import {
  type ReleaseNotificationJob,
  getJobQueueStartedAt,
  getReleaseJobDisplayStatus,
  getReleaseJobTimingText,
} from "./ReleaseNotifications.utils";

type ReleaseJobCardJob = Job & Partial<Pick<ReleaseNotificationJob, "documentName">>;

export interface ReleaseJobCardProps {
  job: ReleaseJobCardJob;
  now?: number;
  className?: string;
  children?: React.ReactNode;
}

const jobStatusToChipVariant = (job: ReleaseJobCardJob): ChipProps["variant"] => {
  if (job.status === "queued" && job.processingStartedAt) {
    return "info";
  }
  switch (job.status) {
    case "completed":
      return "success";
    case "failed":
      return "destructive";
    case "queued":
      return "info";
    default:
      return undefined;
  }
};

export const ReleaseJobCard = ({ job, now = Date.now(), className, children }: ReleaseJobCardProps) => {
  const title = job.title || job.documentName || "Release job";

  return (
    <div className={cn("flex w-full flex-col gap-1 text-normal p-4 border rounded-md", className)}>
      <div className="flex w-full justify-between gap-4">
        <span className="text-xs text-gray-600">{job.description || job.type}</span>

        <Chip
          variant={jobStatusToChipVariant(job)}
          className="text-xs text-gray-600"
        >
          {getReleaseJobDisplayStatus(job)}
        </Chip>
      </div>
      <Tooltip>
        <span
          className="block text-wrap text-sm font-medium"
          title={title}
        >
          {title}
        </span>
        <Tooltip.Content placement="bottom">{title}</Tooltip.Content>
      </Tooltip>
      <div className="flex w-full items-center justify-between gap-4 text-[12px]">
        <Tooltip>
          <div>{job.started && getReleaseJobTimingText(job, now)}</div>
          <Tooltip.Content placement="right">
            Queued {new Date(getJobQueueStartedAt(job)).toLocaleString()}
          </Tooltip.Content>
        </Tooltip>
        <Tooltip>
          <Avatar
            name={job.user?.full_name}
            size="sm"
          />
          <Tooltip.Content placement="left">{job.user?.full_name}</Tooltip.Content>
        </Tooltip>
      </div>
      {children}
    </div>
  );
};
